import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateEvent } from '@/hooks/useEvents';
import type { CreateEventBody } from '@/api/events.api';

/**
 * RojoNegroForm — form de configuración del minijuego "Rojo/Negro" (familia
 * pick). Clona el screenshot de fede: Nombre, Opciones de apuesta (Rojo/Negro
 * con color + emoji + label), Premio por acierto ($), Tipo de premio, Win Rate
 * (%), Duración (min), preview de las fichas y botones Guardar Borrador /
 * Crear y Activar.
 *
 * Genera `config` y llama `eventsApi.create({ type:'rojo_negro', name, config, ... })`
 * vía el hook `useCreateEvent` (mismo patrón que eventos.tsx: toast + invalidación).
 */

interface SideOption {
  side: 'rojo' | 'negro';
  label: string;
  emoji: string;
  color: string;
}

type PrizeKind = 'fichas' | 'bono100' | 'bono200';

const DEFAULT_OPTIONS: SideOption[] = [
  { side: 'rojo', label: 'Rojo', emoji: '🔴', color: '#e53935' },
  { side: 'negro', label: 'Negro', emoji: '⚫', color: '#37474f' },
];

const PRIZE_KIND_LABELS: Record<PrizeKind, string> = {
  fichas: 'Fichas',
  bono100: 'Bono 100%',
  bono200: 'Bono 200%',
};

interface Props {
  /** Callback al crear (cierra el tab / refresca el listado). */
  onCreated?: () => void;
}

export function RojoNegroForm({ onCreated }: Props) {
  const createMutation = useCreateEvent();

  const [name, setName] = useState('');
  const [options, setOptions] = useState<SideOption[]>(DEFAULT_OPTIONS);
  const [prizeAmount, setPrizeAmount] = useState('1000');
  const [prizeKind, setPrizeKind] = useState<PrizeKind>('fichas');
  const [winRate, setWinRate] = useState('50');
  const [durationMin, setDurationMin] = useState('10');
  const [submitting, setSubmitting] = useState(false);

  const updateOption = (i: number, patch: Partial<SideOption>) => {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  };

  const buildBody = (status: 'draft' | 'active'): CreateEventBody => {
    const win = Math.min(100, Math.max(0, Number(winRate) || 0));
    const amount = Number(prizeAmount) || 0;
    return {
      type: 'rojo_negro',
      name: name.trim(),
      prizeKind,
      prizeAmount: amount ? String(amount) : undefined,
      durationMin: Number(durationMin) || 0,
      status,
      config: {
        multiplier: 2,
        // winRate efectivo del jugador (50 = sin ventaja; <50 = ventaja de la casa).
        winRate: win,
        houseEdge: Number(((50 - win) / 50).toFixed(4)),
        prizeKind,
        fixedAmount: amount,
        options,
      },
    };
  };

  const submit = async (status: 'draft' | 'active') => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      await createMutation.mutateAsync(buildBody(status));
      // reset
      setName('');
      setOptions(DEFAULT_OPTIONS);
      setPrizeAmount('1000');
      setPrizeKind('fichas');
      setWinRate('50');
      setDurationMin('10');
      onCreated?.();
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || createMutation.isPending;

  return (
    <div className="space-y-6 p-1">
      <h3 className="text-lg font-semibold text-amber-600">Crear Rojo o Negro</h3>

      {/* Nombre */}
      <div className="space-y-2">
        <Label>
          Nombre <span className="text-red-500">*</span>
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ej: Red Black Night"
        />
      </div>

      {/* Opciones de apuesta */}
      <div className="space-y-2">
        <Label>Opciones de apuesta</Label>
        <div className="space-y-3">
          {options.map((opt, i) => (
            <div
              key={opt.side}
              className="flex items-center gap-3 rounded-lg border border-input p-2"
            >
              {/* swatch de color */}
              <label
                className="relative h-10 w-12 shrink-0 cursor-pointer overflow-hidden rounded-md border"
                style={{ background: opt.color }}
                title="Color"
              >
                <input
                  type="color"
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  value={opt.color}
                  onChange={(e) => updateOption(i, { color: e.target.value })}
                />
              </label>
              {/* preview emoji ficha */}
              <span
                className="flex h-10 w-12 shrink-0 items-center justify-center rounded-md text-xl"
                style={{ background: '#1f2937' }}
              >
                {opt.emoji}
              </span>
              {/* label editable */}
              <Input
                value={opt.label}
                onChange={(e) => updateOption(i, { label: e.target.value })}
                placeholder={opt.side === 'rojo' ? 'Rojo' : 'Negro'}
                className="flex-1"
              />
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" className="w-full" disabled title="Rojo/Negro usa exactamente 2 lados">
          <Plus className="mr-2 h-4 w-4" />
          + Opción
        </Button>
      </div>

      {/* Premio por acierto + tipo de premio */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Premio por acierto ($)</Label>
          <Input
            type="number"
            min="0"
            value={prizeAmount}
            onChange={(e) => setPrizeAmount(e.target.value)}
            placeholder="1000"
          />
        </div>
        <div className="space-y-2">
          <Label>Tipo de premio</Label>
          <Select value={prizeKind} onValueChange={(v) => setPrizeKind(v as PrizeKind)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PRIZE_KIND_LABELS) as PrizeKind[]).map((k) => (
                <SelectItem key={k} value={k}>
                  {PRIZE_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Win Rate + Duración */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>Win Rate (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            value={winRate}
            onChange={(e) => setWinRate(e.target.value)}
            placeholder="50"
          />
          <p className="text-xs text-muted-foreground">
            50% = sin ventaja. Bajalo para darle ventaja a la casa.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Duración (minutos)</Label>
          <Input
            type="number"
            min="0"
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
            placeholder="10"
          />
        </div>
      </div>

      {/* Preview de fichas (como el screenshot) */}
      <div className="flex items-center gap-4">
        {options.map((opt) => (
          <div
            key={opt.side}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl text-white shadow-md"
            style={{ background: opt.color }}
          >
            <span className="text-2xl">{opt.emoji}</span>
            <span className="text-xs font-semibold">{opt.label}</span>
          </div>
        ))}
      </div>

      {/* Acciones */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          disabled={busy || !name.trim()}
          onClick={() => void submit('draft')}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar Borrador
        </Button>
        <Button
          type="button"
          className="bg-amber-500 hover:bg-amber-600"
          disabled={busy || !name.trim()}
          onClick={() => void submit('active')}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear y Activar
        </Button>
      </div>
    </div>
  );
}

export default RojoNegroForm;
